import {PropsWithChildren, useEffect, useState} from 'react';
import {Button, useTheme} from '@mui/material';

import {AuthConfig} from 'core/types';

import {API_HOST, SSO_TENANT_CONFIG_URL, IS_DEV_MODE} from 'config';
import {EnnablLogger} from 'utils/ennabl';

import NoResultsIcon from 'components/icons/placeholders/noResults.svg';
import {Placeholder} from 'components/Placeholder';

import {Auth0Provider} from './Auth0Provider';
import {AzureAuthProvider} from './AzureAuthProvider/AzureAuthProvider';
import {OktaAuthProvider} from './OktaAuthProvider/OktaAuthProvider';

const IS_AUTH0_PROD = window.location.hostname === 'app.ennabl.com';

export const AuthProvider = ({children}: PropsWithChildren) => {
  const theme = useTheme();

  const [authConfig, setAuthConfig] = useState<AuthConfig>();
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (IS_DEV_MODE) {
      return setAuthConfig({authProvider: 'auth0'});
    }

    let retryCount = 3;

    const fetchAuthConfig = async () => {
      if (retryCount === 0) {
        if (IS_AUTH0_PROD) {
          return setAuthConfig({authProvider: 'auth0'});
        }

        setIsError(true);
        return;
      }

      try {
        const res = await fetch(`${API_HOST}${SSO_TENANT_CONFIG_URL}`);
        const data = await res.json();

        if (!res.ok) {
          EnnablLogger.error('Failed to fetch auth config');
          return setAuthConfig({authProvider: 'auth0'});
        }

        setAuthConfig(data);
      } catch (error) {
        retryCount--;
        EnnablLogger.error('An error occurred while fetching authConfig', {error});
        fetchAuthConfig();
      }
    };

    fetchAuthConfig();
  }, []);

  if (isError) {
    return (
      <Placeholder
        minHeight='100dvh'
        icon={<NoResultsIcon color={theme.palette.text.secondary} />}
        heading='We couldn’t load your data'
        subheading='Please reload the page or try again later'
        actions={<Button onClick={() => window.location.reload()}>Reload</Button>}
      />
    );
  }

  if (authConfig?.authProvider === 'azure') {
    return <AzureAuthProvider authConfig={authConfig}>{children}</AzureAuthProvider>;
  }

  if (authConfig?.authProvider === 'okta') {
    return <OktaAuthProvider authConfig={authConfig}>{children}</OktaAuthProvider>;
  }

  if (authConfig?.authProvider === 'auth0') {
    return <Auth0Provider>{children}</Auth0Provider>;
  }

  return children;
};
